import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { UpcomingLiveProps } from '..';
import VariantsGroup from './VariantsGroup';
import { RootState } from '@/redux/store';
import { SliceNameType, Variant } from '@/redux/slices/pdpSlice';
import classnames from 'classnames';
import { usePDPSliceSelectorContext } from '@/hooks/shared/usePDPSliceSelector';

interface VariantListProps {
  labels: UpcomingLiveProps['labels'];
  selectedVariant?: Variant;
  handleSelect: (variant: Variant) => void;
  alreadyOnWaitlistCodes?: Set<string>;
  isWaitlistRegistrationsPending?: boolean;
  withinPanel?: boolean;
}

const memoizedVariantsByType = (sliceName: SliceNameType, type: string) =>
  createSelector(
    [(state: RootState) => state.pdp[sliceName].catalogRelations?.variants],
    variants => variants?.filter(p => p.type === type && !p.registered && !p.isExpired)
  );

const VariantList: React.FC<VariantListProps> = ({
  labels,
  selectedVariant,
  handleSelect,
  alreadyOnWaitlistCodes,
  isWaitlistRegistrationsPending,
  withinPanel = false
}) => {
  const { sliceName } = usePDPSliceSelectorContext();

  const onlineVariants = useSelector(memoizedVariantsByType(sliceName, 'online'));
  const inPersonVariants = useSelector(memoizedVariantsByType(sliceName, 'in-person'));
  const groupcastVariants = useSelector(memoizedVariantsByType(sliceName, 'groupcast'));

  return (
    <div
      className={classnames('flex flex-col gap-10 lg:gap-12', { 'pt-4 lg:pt-6': !withinPanel })}
      data-component="VariantList"
    >
      <VariantsGroup
        icon="global"
        label={labels.online}
        variants={onlineVariants}
        selectedVariant={selectedVariant}
        waitlistLabel={labels.waitlist}
        alreadyOnWaitlistCodes={alreadyOnWaitlistCodes}
        isWaitlistRegistrationsPending={isWaitlistRegistrationsPending}
        onSelect={variant => handleSelect(variant)}
      />
      <VariantsGroup
        icon="location-pin"
        label={labels.inPerson}
        variants={inPersonVariants}
        selectedVariant={selectedVariant}
        waitlistLabel={labels.waitlist}
        alreadyOnWaitlistCodes={alreadyOnWaitlistCodes}
        isWaitlistRegistrationsPending={isWaitlistRegistrationsPending}
        onSelect={variant => handleSelect(variant)}
      />
      <VariantsGroup
        icon="users"
        label={labels.groupcast}
        variants={groupcastVariants}
        selectedVariant={selectedVariant}
        waitlistLabel={labels.waitlist}
        alreadyOnWaitlistCodes={alreadyOnWaitlistCodes}
        isWaitlistRegistrationsPending={isWaitlistRegistrationsPending}
        onSelect={variant => handleSelect(variant)}
      />
    </div>
  );
};

export default VariantList;
